// Dossier magazine — CONTRE-BRAQUAGE À MOTO
// Contenu original signé Bruno · Équipe Code 2 Roues · CODE 2 ROUES · 2026
import {
  Eye, Hand, AlertTriangle, Sparkles, Target, Gauge, Compass,
  ShieldCheck, TrendingUp, Route, HelpCircle, Repeat, Lightbulb,
  RotateCcw, Zap, Bike,
} from 'lucide-react';

export const contreBraquageArticle = {
  slug: 'contre-braquage-moto-methode',
  weekVolume: 37,
  weekYear: 2026,
  monthLabel: 'Septembre 2026',
  category: 'Technique & maîtrise',
  shortTitle: 'Contre-braquage — Maîtriser sa trajectoire',
  title: "Contre-braquage à moto : le comprendre pour vraiment maîtriser sa trajectoire",
  kicker: '🏍️ Dossier technique · Signé Bruno · Équipe Code 2 Roues',
  subtitle:
    "Vous roulez à moto, la route tourne à droite, la moto s'incline naturellement. Mais qu'avez-vous <strong>réellement fait</strong> pour provoquer cette inclinaison ? Probablement quelque chose que vous faites déjà sans même vous en rendre compte : <strong>vous avez contre-braqué</strong>.",
  heroImage: '/images/articles/contre-braquage/00_hero_virage_montagne.jpg',
  heroImageAlt: 'Motard en virage sur route de montagne — illustration du contre-braquage en action, moto inclinée et trajectoire maîtrisée.',
  coverPoster: '/images/articles/contre-braquage/00_cover_virage_route.jpg',
  coverPosterAlt: 'Motard en virée sur route montagneuse — poster CODE 2 ROUES, dossier contre-braquage.',
  topRightKicker: { line1: 'Technique & maîtrise', line2: 'Trajectoire · Évitement' },
  readTimeMin: 11,
  publishedAt: '2026-09-15',
  seoDescription:
    "Contre-braquage à moto : méthode pédagogique complète. Comprendre la mise sur l'angle, le rôle de l'effet gyroscopique, la différence avec le contrepoids, et l'application en évitement. Guide A1/A2 par un moniteur ECSR.",
  seoKeywords:
    'contre-braquage moto, mise sur angle moto, évitement moto, virage moto, trajectoire moto, poussez droite droite, poussez gauche gauche, technique moto avancée, permis A2',
  about: "Dossier pédagogique — comprendre et maîtriser le contre-braquage pour A1/A2 et perfectionnement.",
  chapo: {
    kicker: "La formule à retenir",
    body: "<strong>Je veux aller à droite → je pousse sur la poignée droite. Je veux aller à gauche → je pousse sur la poignée gauche.</strong> Cette action provoque la mise sur l'angle de la moto. Et c'est cette inclinaison qui va lui permettre de s'inscrire dans la courbe.",
    sideStat: {
      kicker: "La formule CODE 2 ROUES",
      big: 'POUSSE À GAUCHE\n↓\nPENCHE À GAUCHE\n↓\nVA À GAUCHE',
      body: "Idem à droite. C'est aussi simple que ça — et vous le faites déjà sans y penser en roulant.",
      accent: '#cf2031',
    },
  },
  pullQuote: {
    text: "Dans un virage, vous avez le temps de préparer votre trajectoire. Face à un obstacle imprévu, vous en avez beaucoup moins. La différence se fera sur votre capacité à regarder l'espace disponible et à y placer immédiatement votre moto.",
    source: 'Bruno · Équipe Code 2 Roues — Enseignant ECSR, CODE 2 ROUES',
  },
  sections: [
    {
      part: 'Partie 1 — La physique',
      title: "Une moto ne tourne pas comme une voiture",
      intro: "À très faible allure (demi-tour, manœuvre), on braque franchement le guidon dans la direction souhaitée. Mais dès que l'allure augmente, la dynamique change complètement — <strong>la moto doit s'incliner pour tourner</strong>.",
      sectionImage: '/images/articles/contre-braquage/05_inclinaison_moto.jpg',
      sectionImageAlt: "Inclinaison d'une moto en virage — la mise sur l'angle à allure routière.",
      compareCards: [
        {
          accent: '#0ea5e9',
          kicker: 'BASSE VITESSE',
          title: 'Le guidon dirige directement',
          intro: "Manœuvres, demi-tour, plateau :",
          points: [
            { ok: true, text: 'Braquage franc du guidon dans la direction' },
            { ok: true, text: 'Regard, point de patinage, frein arrière' },
            { ok: true, text: 'Contrepoids possible (corps opposé)' },
          ],
        },
        {
          accent: '#cf2031',
          kicker: 'ALLURE ROUTIÈRE',
          title: 'La moto doit s\'incliner',
          intro: "Virage réel, changement de trajectoire :",
          points: [
            { ok: true, text: 'Pression sur la poignée = contre-braquage' },
            { ok: true, text: 'Rotation roues + géométrie + pneumatiques + inertie' },
            { ok: true, text: 'Prise d\'angle progressive et contrôlée' },
          ],
        },
      ],
      callouts: [
        {
          icon: Lightbulb,
          accent: '#0ea5e9',
          body: "<strong>La transition entre les deux logiques est progressive</strong> — pas de seuil universel type « à 20 km/h exactement ». Elle dépend de la géométrie de la moto, des pneumatiques, du chargement, et de la précision du conducteur.",
        },
      ],
    },
    {
      part: 'Partie 2 — Le mouvement',
      title: 'Pourquoi parle-t-on de « contre »-braquage ?',
      intro: "Prenons l'exemple : je veux tourner à droite. J'exerce une pression <strong>vers l'avant sur la poignée droite</strong>. Cette action provoque momentanément une très légère orientation de la roue avant <em>vers la gauche</em>. Ça semble contradictoire — d'où le nom.",
      sectionImage: '/images/articles/contre-braquage/07_detail_prise_guidon.jpg',
      sectionImageAlt: "Détail d'une prise souple sur le guidon — gant en appui sur la poignée, finesse de la pression.",
      cards: [
        { icon: Hand, accent: '#cf2031', kicker: '👉 PRESSION', title: 'Sur la poignée droite', body: "Je pousse — pas un grand mouvement, une pression contrôlée vers l'avant." },
        { icon: RotateCcw, accent: '#f97316', kicker: '⬅️ ROUE', title: 'S\'oriente momentanément à gauche', body: "Le point de contact de la roue avant se déplace par rapport à l'ensemble moto-conducteur." },
        { icon: Bike, accent: '#16a34a', kicker: '↘️ MOTO', title: 'Bascule et s\'incline à droite', body: "Le roulis se déclenche, la moto prend l'angle nécessaire pour s'inscrire dans la courbe." },
      ],
      cardsCols: 3,
      callouts: [
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          body: "<strong>Attention</strong> : ce n'est PAS un mouvement brutal de guidon. C'est une <em>pression</em> précise et adaptée. Un coup de guidon violent déstabilise la moto au lieu de la placer.",
        },
      ],
    },
    {
      part: 'Partie 3 — La séquence',
      title: 'Décomposons le mouvement en 5 étapes',
      sectionImage: '/images/articles/contre-braquage/03_vue_guidon_et_commandes.jpg',
      sectionImageAlt: 'Vue sur le guidon et les commandes d\'une moto en roulage — illustration des 5 étapes du contre-braquage : regard, pression sur la poignée, orientation momentanée de la roue, prise d\'angle, stabilisation.',
      orderedList: [
        "<strong>Étape 1 — Je regarde.</strong> Je cherche ma trajectoire et ma sortie. Le regard ne fait pas physiquement tourner la moto, mais il permet au cerveau d'organiser toutes les actions nécessaires.",
        "<strong>Étape 2 — Je pousse.</strong> Je veux aller à droite → je pousse à droite. Je veux aller à gauche → je pousse à gauche. Il s'agit d'une pression sur le guidon, pas d'un grand mouvement brutal.",
        "<strong>Étape 3 — La roue se déplace momentanément.</strong> Pour un virage à droite, l'action produit une orientation de la roue vers la gauche. Le point d'appui se déplace, l'ensemble moto-conducteur commence à prendre de l'angle du côté opposé (à droite).",
        "<strong>Étape 4 — La moto prend de l'angle.</strong> Une fois inclinée, elle peut s'inscrire dans sa courbe. Le profil des pneumatiques et les forces latérales (dont la <em>poussée de carrossage</em>) participent directement au changement de trajectoire.",
        "<strong>Étape 5 — Je stabilise.</strong> Une fois l'angle nécessaire obtenu, je n'augmente évidemment pas indéfiniment ma pression. J'adapte mon action pour conserver angle + trajectoire + allure. La moto est inscrite dans le virage.",
      ],
    },
    {
      part: 'Partie 4 — L\'effet gyroscopique',
      title: 'Précisions importantes sans devenir techniquement faux',
      intro: "Une roue de moto en rotation possède un moment cinétique — elle présente donc un <strong>comportement gyroscopique</strong>. Plus sa vitesse est importante, plus ses effets sont perceptibles. C'est pourquoi une moto arrêtée est instable et une moto qui roule assez vite est très stable.",
      sectionImage: '/images/articles/contre-braquage/04_moto_en_virage.jpg',
      sectionImageAlt: 'Moto inclinée en virage — l\'effet gyroscopique et la mise sur l\'angle en action, roue avant en rotation participant à la stabilité dynamique.',
      compareCards: [
        {
          accent: '#cf2031',
          kicker: '❌ RACCOURCIS À ÉVITER',
          title: 'Ne pas dire',
          intro: "Deux affirmations trop simplistes :",
          points: [
            { ok: false, text: 'Une moto tient debout uniquement grâce à l\'effet gyroscopique' },
            { ok: false, text: 'Le contre-braquage sert à annuler l\'effet gyroscopique' },
          ],
        },
        {
          accent: '#16a34a',
          kicker: '✅ FORMULATION JUSTE',
          title: 'Ce qui se passe vraiment',
          intro: "Tout fonctionne ensemble :",
          points: [
            { ok: true, text: 'La roue avant change momentanément de direction' },
            { ok: true, text: 'Son point de contact se déplace' },
            { ok: true, text: 'Des efforts latéraux apparaissent au pneumatique' },
            { ok: true, text: 'La réponse gyroscopique participe à la mise sur l\'angle' },
            { ok: true, text: 'L\'ensemble moto-conducteur prend du roulis' },
          ],
        },
      ],
      callouts: [
        {
          icon: Lightbulb,
          accent: '#0ea5e9',
          body: "Pour l'élève, inutile d'apprendre un cours de mécanique. Il doit surtout comprendre : <strong>plus la moto possède de stabilité dynamique, plus je dois agir précisément sur le guidon pour commander sa mise sur l'angle</strong>.",
        },
      ],
    },
    {
      part: 'Partie 5 — Ne pas confondre',
      title: 'Contre-braquage ≠ Contrepoids',
      intro: "Nous avons déjà parlé du <strong>contrepoids</strong> dans le dossier consacré aux manœuvres à basse vitesse (demi-tour, plateau). Les deux termes sont proches. Les techniques sont différentes.",
      sectionImage: '/images/articles/contre-braquage/08_position_jambes_genoux.jpg',
      sectionImageAlt: "Position des jambes et genoux contre le réservoir — l'ancrage corporel du pilote, à distinguer du contre-braquage au guidon.",
      compareCards: [
        {
          accent: '#7c3aed',
          kicker: '🧍 CONTREPOIDS',
          title: 'Corps opposé, basse vitesse',
          intro: "Le conducteur déplace sa masse relativement à la moto :",
          points: [
            { ok: true, text: 'Moto inclinée vers l\'intérieur du virage lent' },
            { ok: true, text: 'Buste conducteur reste vers l\'extérieur' },
            { ok: true, text: 'Utilisé pour demi-tour, plateau, manœuvres serrées' },
            { ok: true, text: 'CONTREPOIDS = CORPS' },
          ],
        },
        {
          accent: '#cf2031',
          kicker: '👐 CONTRE-BRAQUAGE',
          title: 'Guidon, allure routière',
          intro: "Le conducteur exerce une action sur le guidon :",
          points: [
            { ok: true, text: 'Pression sur la poignée du côté du virage' },
            { ok: true, text: 'Provoque ou modifie la mise sur l\'angle' },
            { ok: true, text: 'Utilisé en virage routier, évitement, changement rapide' },
            { ok: true, text: 'CONTRE-BRAQUAGE = GUIDON' },
          ],
        },
      ],
    },
    {
      part: 'Partie 6 — L\'application',
      title: 'En virage vs en évitement — deux usages, deux logiques',
      intro: "Dans un virage, vous avez le temps de préparer votre trajectoire. Face à un obstacle imprévu, vous en avez beaucoup moins. Le contre-braquage doit donc devenir <strong>conscient PUIS automatique</strong>.",
      sectionImage: '/images/articles/contre-braquage/06_trajectoire_en_courbe.jpg',
      sectionImageAlt: 'Trajectoire d\'une moto en courbe — illustration de la différence entre virage préparé et évitement d\'urgence (deux contre-braquages successifs).',
      cards: [
        { icon: Route, accent: '#0ea5e9', kicker: 'EN VIRAGE', title: 'Préparation anticipée', body: "J'adapte mon allure AVANT le virage, je regarde ma trajectoire À L'ENTRÉE, je pousse pour déclencher, je stabilise DANS LA COURBE, j'accompagne le redressement À LA SORTIE." },
        { icon: AlertTriangle, accent: '#cf2031', kicker: 'ÉVITEMENT', title: 'Deux contre-braquages successifs', body: "Obstacle devant, éviter à droite : 1) pression à droite → la moto change de trajectoire. 2) Action opposée → la moto se replace. DROITE → GAUCHE (ou l'inverse)." },
        { icon: Gauge, accent: '#f97316', kicker: 'FREINER OU ÉVITER ?', title: 'Le capital adhérence', body: "En freinage fort, le pneu utilise déjà une partie de son adhérence. Un changement rapide demande AUSSI des efforts latéraux. Parfois il faut PRIVILÉGIER : freinage PUIS évitement." },
      ],
      cardsCols: 3,
      callouts: [
        {
          icon: ShieldCheck,
          accent: '#16a34a',
          body: "<strong>Un évitement propre, c'est deux contre-braquages successifs.</strong> Cette prise de conscience change complètement la façon d'appréhender une manœuvre d'urgence.",
        },
      ],
    },
    {
      part: 'Partie 7 — La pratique',
      title: '4 exercices progressifs — la progression CODE 2 ROUES',
      intro: "Progression pédagogique. Chaque exercice construit le suivant — <strong>toujours dans un cadre sécurisé et, pour un élève, sous l'encadrement de son enseignant</strong>.",
      sectionImage: '/images/articles/contre-braquage/09_pied_sur_repose_pied.jpg',
      sectionImageAlt: "Pied correctement positionné sur le repose-pied — base stable pour les exercices progressifs de contre-braquage.",
      orderedList: [
        "<strong>Exercice 1 — Je ressens.</strong> Sur zone fermée avec votre enseignant, en ligne droite à une allure définie, bras détendus, regard loin. Puis : légère pression à droite → observez. La moto commence à prendre de l'angle. Revenez tranquillement. Puis à gauche. Objectif : uniquement ressentir la relation <em>pression → inclinaison</em>. Pas de vitesse, pas de performance.",
        "<strong>Exercice 2 — Je contrôle.</strong> Slalom suffisamment large. À chaque changement de direction : regard → pression → inclinaison → regard suivant → pression opposée → inclinaison. Objectif : rendre les changements FLUIDES, PRÉCIS, RÉGULIERS.",
        "<strong>Exercice 3 — Je place ma moto.</strong> Matérialisez deux trajectoires parallèles. À un repère donné : regard sur l'espace libre → je contre-braque → je déplace la moto → j'agis dans l'autre sens → je stabilise ma nouvelle trajectoire. On ne cherche plus à faire tourner la moto — on apprend à la PLACER.",
        "<strong>Exercice 4 — L'évitement.</strong> Cadre pédagogique sécurisé UNIQUEMENT. Reproduire progressivement un changement rapide de trajectoire : REGARD → PREMIÈRE IMPULSION → CHANGEMENT D'ANGLE → IMPULSION OPPOSÉE → STABILISATION. Jamais seul, jamais sur route ouverte.",
      ],
      callouts: [
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          body: "<strong>Aucune de ces techniques ne s'improvise sur route ouverte.</strong> Un article pédagogique explique la technique — il n'incite JAMAIS à tester ses limites sans encadrement. La sécurité passe avant la démonstration.",
        },
      ],
    },
    {
      part: 'Partie 8 — Les erreurs',
      title: 'Les 6 erreurs classiques à éviter',
      sectionImage: '/images/articles/contre-braquage/02_main_sur_poignee.jpg',
      sectionImageAlt: 'Main gantée sur la poignée du guidon — illustration des erreurs à éviter : pousser brutalement, se crisper, confondre contrepoids et contre-braquage.',
      cards: [
        { icon: Hand, accent: '#cf2031', kicker: '❌ 1', title: 'Pousser brutalement', body: "On recherche une action ADAPTÉE, pas un coup de guidon violent qui déstabilise la moto." },
        { icon: Sparkles, accent: '#cf2031', kicker: '❌ 2', title: 'Se crisper sur le guidon', body: "Des bras rigides diminuent la finesse de commande. Bras SOUPLES = pilotage précis." },
        { icon: Eye, accent: '#cf2031', kicker: '❌ 3', title: 'Regarder l\'obstacle', body: "Je détecte le danger PUIS je recherche l'espace disponible. Le regard sur l'obstacle = trajectoire vers l'obstacle." },
        { icon: Repeat, accent: '#cf2031', kicker: '❌ 4', title: 'Tourner uniquement avec le corps', body: "Le déplacement du corps influence l'ensemble, mais à allure routière l'action au guidon est DÉTERMINANTE pour commander rapidement le roulis." },
        { icon: HelpCircle, accent: '#cf2031', kicker: '❌ 5', title: 'Confondre contrepoids et contre-braquage', body: "Deux techniques DIFFÉRENTES pour deux problématiques différentes. Corps ≠ Guidon. Basse vitesse ≠ Allure routière." },
        { icon: AlertTriangle, accent: '#cf2031', kicker: '❌ 6', title: 'Découvrir le geste le jour d\'une urgence', body: "Une technique de sécurité doit être APPRISE et RESSENTIE AVANT d'en avoir réellement besoin. Après l'urgence il est trop tard." },
      ],
      cardsCols: 3,
    },
    {
      part: 'À retenir',
      title: 'La méthode CODE 2 ROUES en 5 réflexes',
      sectionImage: '/images/articles/contre-braquage/01_regard_et_helmet.jpg',
      sectionImageAlt: "Casque et regard du motard — point de départ de toute action de pilotage : je regarde où je veux aller.",
      cards: [
        { icon: Eye, accent: '#0ea5e9', kicker: '👀', title: 'JE REGARDE', body: 'Je cherche ma trajectoire et l\'espace disponible.' },
        { icon: Hand, accent: '#cf2031', kicker: '👐', title: 'JE POUSSE', body: 'Pression adaptée sur la poignée du côté du virage.' },
        { icon: Bike, accent: '#7c3aed', kicker: '🏍️', title: 'JE PROVOQUE L\'INCLINAISON', body: 'La moto prend l\'angle nécessaire pour s\'inscrire.' },
        { icon: Target, accent: '#16a34a', kicker: '🎯', title: 'JE PLACE MA MOTO', body: 'J\'oriente ma trajectoire précisément où je le veux.' },
        { icon: TrendingUp, accent: '#f59e0b', kicker: '🧠', title: 'JE STABILISE', body: 'Je maintiens angle + trajectoire + allure jusqu\'à la sortie.' },
      ],
      cardsCols: 5,
      callouts: [
        {
          icon: Sparkles,
          accent: '#cf2031',
          body: "<strong>Pousse à gauche → penche à gauche → va à gauche. Pousse à droite → penche à droite → va à droite.</strong> C'est simple, efficace, et ça marche à tous les coups à allure routière.",
        },
      ],
    },
  ],
  ctaFinal: {
    kicker: 'Pratique guidée',
    title: 'Consulte aussi les dossiers Trajectoire & Demi-tour',
    body: "Le <strong>contre-braquage</strong> complète les dossiers <strong>Trajectoire de sécurité en virage</strong> (allure routière) et <strong>Demi-tour</strong> (basse vitesse, contrepoids). Ensemble, ils couvrent la totalité de ta relation à la trajectoire moto.",
    primary: { label: 'Lire le dossier Trajectoire', to: '/magazine/trajectoire-securite-virage' },
    secondary: { label: 'Voir le dossier Demi-tour', to: '/magazine/demi-tour-moto-methode' },
  },
};
